import debounce from 'lodash-es/debounce';
import get from 'lodash-es/get';
import {
  computed,
  effectScope,
  nextTick,
  reactive,
  readonly,
  type Ref,
  ref,
  toValue,
  type UnwrapRef,
  watch,
} from 'vue';

import { DEBOUNCE } from '../../constants';
import { parseJsonApiFieldErrors } from './parseJsonApiFieldErrors';
import type {
  JsonApiValidationError,
  UseValidationArgs,
  ValidationError,
  ValidationField,
  ValidationGroup,
  ValidationRule,
} from './useValidation.types';

const API_ERROR_RULE_NAME = 'api';

function isValidationRule(x: unknown): x is ValidationRule {
  if (typeof x !== 'object' || x === null) {
    return false;
  }
  if (!('validator' in x)) {
    return false;
  }
  return typeof Reflect.get(x, 'validator') === 'function';
}

function isValidationRuleArray(value: unknown): value is ValidationRule[] {
  return Array.isArray(value) && value.every(isValidationRule);
}

/**
 * Lists dotted field paths for useValidation. Unlike `flatten(..., { safe: true })`, this descends into
 * arrays whose elements are per-row rule objects (e.g. `terpenes: [{ name: [...], amount: [...] }]`).
 * Arrays whose first element looks like a `ValidationRule` are treated as a single field's rule list.
 * An empty rule array at a path (e.g. `optionalField: []`) still registers that field so `touch`, API
 * errors, and dirty state work.
 */
function collectValidationFieldPaths(value: unknown, prefix = ''): string[] {
  if (value === null || typeof value !== 'object') {
    return [];
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return prefix ? [prefix] : [];
    }
    const first = value[0];
    if (isValidationRule(first)) {
      return prefix ? [prefix] : [];
    }
    const paths: string[] = [];
    for (let i = 0; i < value.length; i++) {
      const p = prefix === '' ? String(i) : `${prefix}.${i}`;
      paths.push(...collectValidationFieldPaths(value[i], p));
    }
    return paths;
  }

  const paths: string[] = [];
  for (const key of Object.keys(value)) {
    const child = (value as Record<string, unknown>)[key];
    const p = prefix === '' ? key : `${prefix}.${key}`;
    paths.push(...collectValidationFieldPaths(child, p));
  }
  return paths;
}

/** Per-field teardown: value watcher, debounce, and `buildField` effectScope (stops its computeds). */
interface FieldCleanup {
  stop: () => void;
  cancelDebounced: () => void;
  stopFieldScope: () => void;
}

export default function useValidation<Values extends object>({
  rules,
  values,
}: UseValidationArgs<Values>): ValidationGroup<Values> {
  const apiErrors = ref<Record<string, string>>({});

  const fields = reactive<Record<string, ValidationField>>({});
  const fieldCleanups = new Map<string, FieldCleanup>();

  // Sort so traversal order is stable; the computed caches one sorted list per rules invalidation.
  const sortedRuleFieldNames = computed(() => collectValidationFieldPaths(toValue(rules)).sort());

  function removeField(fieldName: string) {
    const cleanup = fieldCleanups.get(fieldName);
    if (cleanup) {
      cleanup.cancelDebounced();
      cleanup.stop();
      cleanup.stopFieldScope();
      fieldCleanups.delete(fieldName);
    }
    if (fields[fieldName]) {
      delete fields[fieldName];
    }
    if (apiErrors.value[fieldName]) {
      const next = { ...apiErrors.value };
      delete next[fieldName];
      apiErrors.value = next;
    }
  }

  function addField(fieldName: string) {
    if (fields[fieldName]) {
      return;
    }

    const fieldValue = ref(get(toValue(values), fieldName));

    const updateFieldValue = () => {
      fieldValue.value = get(toValue(values), fieldName);
    };

    const debouncedSetFieldValue = debounce(() => {
      updateFieldValue();
    }, DEBOUNCE.FAST);

    const stop = watch(
      () => get(toValue(values), fieldName),
      () => {
        debouncedSetFieldValue();
      },
    );

    // Computeds inside `buildField` subscribe to rules/apiErrors; scope.stop() drops those subscriptions when the row is removed.
    const fieldScope = effectScope();
    const builtField = fieldScope.run(() =>
      buildField<UnwrapRef<typeof fieldValue>>({
        fieldName,
        fieldValue,
        flush: () => {
          debouncedSetFieldValue.cancel();
          updateFieldValue();
        },
        getApiError: () => apiErrors.value[fieldName] || '',
        getRules: () => {
          const raw = get(toValue(rules), fieldName);
          return isValidationRuleArray(raw) ? raw : [];
        },
      }),
    );

    if (!builtField) {
      stop();
      debouncedSetFieldValue.cancel();
      fieldScope.stop();
      return;
    }

    fieldCleanups.set(fieldName, {
      stop,
      cancelDebounced: () => debouncedSetFieldValue.cancel(),
      stopFieldScope: () => fieldScope.stop(),
    });

    fields[fieldName] = builtField;
  }

  watch(
    sortedRuleFieldNames,
    (newNames, oldNames) => {
      const prev = oldNames ?? [];
      const newSet = new Set(newNames);
      const oldSet = new Set(prev);

      for (const name of prev) {
        if (!newSet.has(name)) {
          removeField(name);
        }
      }
      for (const name of newNames) {
        if (!oldSet.has(name)) {
          addField(name);
        }
      }
    },
    { immediate: true },
  );

  const fieldList = computed(() => Object.values(fields));

  const hasErrors = computed(() => fieldList.value.some((field) => field.errors.length > 0));

  const someTouched = computed(() => fieldList.value.some((field) => field.isTouched));

  const dirtyFields = computed(() => fieldList.value.filter((field) => field.isDirty));

  const validate: ValidationGroup<Values>['validate'] = async function ({ preventScroll } = {}) {
    fieldList.value.forEach((field) => {
      // Doing setTouched(true) on a field that already has isTouched as `true` will not trigger reactivity for that field. Therefore, setting isTouched to `false` for all fields ensures that setTouched(true) will trigger reactivity for every field, which will cause all validations to run and display any error messages.
      field.setTouched(false);
      field.setTouched(true);
    });
    await nextTick();

    if (hasErrors.value && !preventScroll) {
      const firstFieldWithError = Object.entries(fields).find(([, field]) => field.errorMessage);
      const fieldName = firstFieldWithError?.[0] || '';
      const firstErrorElement = document?.querySelector(`[name="${fieldName}"]`);

      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }

    return !hasErrors.value;
  };

  const getError: ValidationGroup<Values>['getError'] = function (fieldName: string) {
    return fields[fieldName]?.errorMessage || '';
  };

  const touch: ValidationGroup<Values>['touch'] = function (fieldName: string) {
    if (!fields[fieldName]) {
      throw new Error(`In touch: fieldName "${fieldName}" does not exist in the validation fields.`);
    }

    fields[fieldName].setTouched();
  };

  const setAllUntouched: ValidationGroup<Values>['setAllUntouched'] = async function () {
    await nextTick(); // wait for field values to be reset
    fieldList.value.forEach((field) => {
      field.setTouched(false);
    });
  };

  const setInitialValues: ValidationGroup<Values>['setInitialValues'] = function (initialValues: Values) {
    fieldList.value.forEach((field) => {
      field.setInitialValue(get(initialValues, field.name));
    });
  };

  function setApiErrors(errors: JsonApiValidationError[]) {
    const parsed = parseJsonApiFieldErrors(errors);
    const known = sortedRuleFieldNames.value;
    const next: Record<string, string> = {};
    for (const name of known) {
      if (parsed[name]) {
        next[name] = parsed[name];
      }
    }
    apiErrors.value = next;
  }

  function clearApiErrors(fieldName?: string) {
    if (fieldName) {
      const next = { ...apiErrors.value };
      delete next[fieldName];
      apiErrors.value = next;
    } else {
      apiErrors.value = {};
    }
  }

  return reactive({
    validate,
    getError,
    touch,
    setAllUntouched,
    setInitialValues,
    setApiErrors,
    clearApiErrors,
    fields,
    dirtyFields,
    hasErrors,
    someTouched,
  });
}

interface BuildFieldArgs<Value = unknown> {
  fieldName: string;
  fieldValue: Ref<Value>;
  getRules: () => ValidationRule[];
  flush: () => void;
  getApiError: () => string;
}

function buildField<Value = unknown>({
  fieldName,
  fieldValue,
  getRules,
  flush,
  getApiError,
}: BuildFieldArgs<Value>): ValidationField<Value> {
  /**
   * In order for `initialValue` to work properly, `initialValue` is captured when the field is registered; `setInitialValue` updates it thereafter.
   */
  const initialValue = ref(fieldValue.value) as Ref<Value>;
  const isTouched = ref(false);
  const isDirty = computed(() => (fieldValue.value ?? '') !== (initialValue.value ?? ''));

  const results = computed(() => {
    // Adding a check for `isTouched` to avoid running the validation rules on the initial load, when the field is not touched.
    // if receives a fieldValue with some value as default or not, rule validation should also be triggered only via `touch()` or `validate()` methods.
    if (!isTouched.value) {
      return [];
    }

    const rulesList = getRules();
    return rulesList.map((rule) => ({
      fieldName,
      ruleName: rule.name,
      isValid: rule.validator(fieldValue.value),
      message: typeof rule.message === 'function' ? rule.message(fieldValue.value) : rule.message,
    }));
  });

  const errors = computed<ValidationError[]>(() => {
    const apiMessage = getApiError();
    const apiErrorList: ValidationError[] =
      apiMessage !== '' ? [{ ruleName: API_ERROR_RULE_NAME, message: apiMessage }] : [];
    const ruleErrors = results.value
      .filter((result) => !result.isValid)
      .map((result) => ({
        ruleName: result.ruleName,
        message: result.message,
      }));
    return [...apiErrorList, ...ruleErrors];
  });

  function setTouched(value = true) {
    // Flushing the debounced value to be updated right away.
    // So that, we can have reactivity for `rules` validation, `isTouched`, `isDirty`, among other values
    flush();

    isTouched.value = value;
  }

  function setInitialValue(value: Value) {
    initialValue.value = value;
  }

  return reactive({
    name: fieldName,
    currentValue: readonly(fieldValue),
    initialValue: readonly(initialValue),
    isTouched: readonly(isTouched),
    isDirty: readonly(isDirty),
    errorMessage: computed(() => errors.value?.[0]?.message || ''),
    errors,
    setInitialValue,
    setTouched,
  });
}

// The ruleFactories are excluded from the main entrypoint in vite.config.ts, so they are exported here instead. This allows one import statement to be used for useValidation, its types, and all of the ruleFactories.
export { default as email } from './ruleFactories/email';
export { default as maxLength } from './ruleFactories/maxLength';
export { default as maxValue } from './ruleFactories/maxValue';
export { default as minLength } from './ruleFactories/minLength';
export { default as minValue } from './ruleFactories/minValue';
export { default as pattern } from './ruleFactories/pattern';
export { default as positiveNumber } from './ruleFactories/positiveNumber';
export { default as price } from './ruleFactories/price';
export { default as required } from './ruleFactories/required';
export { default as wholeNumber } from './ruleFactories/wholeNumber';
export * from './useValidation.types';
export { default as isDefined } from './utils/isDefined';
